import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import WorshipLayout from '@/components/WorshipLayout';
import WorshipPermissionControls from '@/components/WorshipPermissionControls';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import ChordProEditor from '@/components/chordpro/ChordProEditor';
import ChordProRenderer from '@/components/chordpro/ChordProRenderer';
import WorshipImportWizard from '@/components/chordpro/WorshipImportWizard';
import WorshipRosterSection from '@/features/worship/roster/WorshipRosterSection';
import SetlistPanel from '@/features/worship/setlist/SetlistPanel';
import { formatRosterMonth, shiftRosterMonth as shiftRosterMonthValue } from '@/features/worship/roster/rosterUtils';
import { buildSongPayload, cacheWorshipData, getCachedWorshipData, parseChordPro } from '@/chordpro/index';
import { parseStructuredSong, serializeStructuredSong } from '@/chordpro/structuredSongAdapter';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import {
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Copy,
  ArrowLeft,
  Files,
  FileUp,
  Eye,
  Pencil,
  MapPin,
  MessageCircle,
  Music2,
  Plus,
  Repeat,
  Search,
  List,
  LayoutGrid,
  Settings,
  Share2,
  SlidersHorizontal,
  ShieldAlert,
  Trash2,
  UserCheck,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FUNCTIONS = [
  'Worshipleader', 'Lead vocal', 'Backing vocal', 'Piano', 'Keyboard',
  'Akoestische gitaar', 'Elektrische gitaar', 'Bas', 'Drums', 'Percussie',
  'Geluid', 'Beamer', 'Livestream', 'Camera', 'Licht',
];
const STATUS_LABELS = {
  concept: 'Concept', gepland: 'Gepland', bevestigd: 'Bevestigd',
  afgerond: 'Afgerond', geannuleerd: 'Geannuleerd',
};
const RESPONSE_LABELS = {
  uitgenodigd: 'Uitgenodigd', bevestigd: 'Bevestigd', afgewezen: 'Afgewezen',
  onder_voorbehoud: 'Onder voorbehoud',
};
const WEEKDAYS = [
  { value: 0, label: 'Ma' }, { value: 1, label: 'Di' }, { value: 2, label: 'Wo' },
  { value: 3, label: 'Do' }, { value: 4, label: 'Vr' }, { value: 5, label: 'Za' }, { value: 6, label: 'Zo' },
];
const emptyPreferences = {
  target_per_month: '', min_per_month: '', max_per_month: '',
  eligible_functions: [], unavailable_weekdays: [], preferred_service_time: '', notes: '',
};
const emptyAbsence = { start_date: '', end_date: '', reason: '' };
const emptyPattern = {
  title: '', weekday: 6, start_time: '10:00', end_time: '11:30',
  location: '', default_worship_leader_id: '', is_active: true,
};
const currentYearMonth = () => {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
};
const emptyGenerateForm = { ...currentYearMonth(), skip_dates: '' };
const CONFLICT_MODULE_LABELS = {
  worship: 'Worship', kinderwerk: 'Kinderwerk', techniek: 'Techniek', sprekers: 'Sprekers',
  connectgroepen: 'Connectgroep', afwezigheid: 'Afwezigheid',
};
const WORSHIP_ROLE_LABELS = {
  admin: 'Beheerder',
  worshipleader: 'Worshipleader',
  coordinator_worship: 'Worshipcoördinator',
  worshipteamlid: 'Worshipteamlid',
  pastor: 'Voorganger',
};
const worshipRoleLabel = (role) => WORSHIP_ROLE_LABELS[role] || role;
const monthLabel = formatRosterMonth;

const AccessDeniedState = ({ onBack }) => (
  <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
    <Card className="w-full max-w-lg border-0 shadow-sm">
      <CardContent className="p-8 text-center sm:p-10">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-50 text-amber-700">
          <ShieldAlert className="h-7 w-7" />
        </div>
        <h1 className="mt-5 text-xl font-semibold text-slate-900">Geen toegang tot Worship Planning</h1>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Je account heeft geen toegang tot dit onderdeel. Vraag de Worshipleader of beheerder om Worship Planning aan je toe te wijzen.
        </p>
        <Button variant="outline" className="mt-6" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Terug naar Worship
        </Button>
      </CardContent>
    </Card>
  </div>
);

const emptyService = {
  title: '', service_date: '', start_time: '10:00', end_time: '11:30',
  location: '', theme: '', speaker: '', worship_leader_id: '', zangdienst_user_id: '', status: 'concept', notes: '',
};
const newChordPro = `{title: Nieuw lied}\n{artist: }\n{key: C}\n{tempo: 72}\n{language: Nederlands}\n\n{start_of_verse: Vers 1}\n[C]Schrijf hier de [G]liedtekst\n{end_of_verse}\n\n{start_of_chorus: Refrein}\n[F]Schrijf hier het [C]refrein\n{end_of_chorus}\n`;
const emptySongIntegrations = {
  songselect_id: '', publisher: '', license: '', source: '',
  google_drive_file_id: '', google_drive_folder_id: '',
  pdf_link_url: '', pdf_link_label: '',
};
const emptyRehearsal = {
  rehearsal_date: '', start_time: '19:30', end_time: '21:30', location: '', service_id: '', song_ids: [], notes: '',
};

const formatDate = (value) => value
  ? new Intl.DateTimeFormat('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(`${value}T12:00:00`))
  : '-';

const SectionHeader = ({ title, description, action }) => (
  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
    <div>
      <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
      <p className="mt-1 text-slate-500">{description}</p>
    </div>
    {action}
  </div>
);

const EmptyState = ({ icon: Icon, title, text }) => (
  <div className="py-12 text-center">
    <Icon className="mx-auto mb-3 h-10 w-10 text-slate-300" />
    <p className="font-medium text-slate-800">{title}</p>
    <p className="mt-1 text-sm text-slate-500">{text}</p>
  </div>
);

const ServiceCard = ({ service, onOpen, ownAssignment }) => (
  <button type="button" onClick={() => onOpen(service)} className="w-full rounded-md border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:border-teal-300 hover:shadow-md">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate font-semibold text-slate-900">{service.title}</p>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
          <span className="flex items-center gap-1"><CalendarDays className="h-4 w-4" />{formatDate(service.service_date)}</span>
          <span className="flex items-center gap-1"><Clock3 className="h-4 w-4" />{service.start_time} - {service.end_time}</span>
          {service.location && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{service.location}</span>}
        </div>
      </div>
      <Badge variant="secondary">{STATUS_LABELS[service.status] || service.status}</Badge>
    </div>
    <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 text-sm">
      <span className="text-slate-500">{service.assignments?.length || 0} teamleden · {service.setlist?.length || 0} onderdelen</span>
      {ownAssignment && <span className="font-medium text-teal-700">{ownAssignment.function}</span>}
    </div>
  </button>
);

const serviceDateKey = (value) => value || '';
const formatCompactServiceDate = (value) => value
  ? new Intl.DateTimeFormat('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(`${value}T12:00:00`))
  : '-';
const formatMonthHeading = (value) => value
  ? new Intl.DateTimeFormat('nl-NL', { month: 'long', year: 'numeric' }).format(new Date(`${value}T12:00:00`))
  : '';
const localDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
const currentDateKey = () => localDateKey(new Date());
const currentMonthKey = () => currentDateKey().slice(0, 7);

const CompactServiceRow = ({ service, onOpen, ownAssignment }) => (
  <button
    type="button"
    onClick={() => onOpen(service)}
    className="group grid w-full gap-3 border-b border-slate-100 px-4 py-4 text-left transition last:border-0 hover:bg-teal-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-500 sm:grid-cols-[120px_minmax(0,1.5fr)_minmax(150px,1fr)_auto_auto] sm:items-center sm:px-5"
  >
    <div className="flex items-center gap-2 text-sm font-semibold capitalize text-slate-800">
      <CalendarDays className="h-4 w-4 shrink-0 text-teal-600" />
      <span>{formatCompactServiceDate(service.service_date)}</span>
      <span className="font-normal text-slate-500 sm:hidden">· {service.start_time}</span>
    </div>
    <div className="min-w-0 pl-6 sm:pl-0">
      <p className="truncate font-semibold text-slate-900">{service.title}</p>
      <p className="mt-0.5 truncate text-sm text-slate-500">{service.start_time}–{service.end_time}{service.location ? ` · ${service.location}` : ''}</p>
    </div>
    <div className="flex items-center gap-3 pl-6 text-sm text-slate-500 sm:pl-0">
      <span>{service.assignments?.length || 0} teamleden</span>
      <span className="text-slate-300">·</span>
      <span>{service.setlist?.length || 0} onderdelen</span>
      {ownAssignment && <span className="hidden text-teal-700 lg:inline">· {ownAssignment.function}</span>}
    </div>
    <Badge variant="secondary" className="ml-6 w-fit sm:ml-0">{STATUS_LABELS[service.status] || service.status}</Badge>
    <ChevronRight className="hidden h-5 w-5 text-slate-300 transition group-hover:text-teal-600 sm:block" />
  </button>
);

const formatSetlistUpdatedAt = (value) => value ? (() => {
  const updated = new Date(value);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const dateKey = (date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  const time = new Intl.DateTimeFormat('nl-NL', { hour: '2-digit', minute: '2-digit' }).format(updated);
  if (dateKey(updated) === dateKey(today)) return `Vandaag, ${time}`;
  if (dateKey(updated) === dateKey(yesterday)) return `Gisteren, ${time}`;
  return new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(updated);
})() : null;

const getSetlistSummary = (service) => {
  const items = service.setlist || [];
  const songs = items.filter((item) => item.song_id);
  const openItems = items.filter((item) => !item.title?.trim());
  return { items, songs, openItems };
};

const CompactSetlistRow = ({ service, onOpen }) => {
  const { items, songs, openItems } = getSetlistSummary(service);
  const hasSetlist = items.length > 0;
  const updatedAt = formatSetlistUpdatedAt(service.updated_at);
  return (
    <button
      type="button"
      onClick={() => onOpen(service)}
      className="group grid w-full cursor-pointer gap-3 border-b border-slate-100 px-4 py-4 text-left transition last:border-0 hover:bg-teal-50/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-500 sm:grid-cols-[120px_minmax(0,1.5fr)_minmax(170px,1fr)_auto_auto] sm:items-center sm:px-5"
    >
      <div className="flex items-center gap-2 text-sm font-semibold capitalize text-slate-800">
        <CalendarDays className="h-4 w-4 shrink-0 text-teal-600" />
        <span>{formatCompactServiceDate(service.service_date)}</span>
        <span className="font-normal text-slate-500 sm:hidden">· {service.start_time}</span>
      </div>
      <div className="min-w-0 pl-6 sm:pl-0">
        <p className="truncate font-semibold text-slate-900">{service.title}</p>
        <p className="mt-0.5 truncate text-sm text-slate-500">{service.location || 'Locatie volgt'}</p>
        <p className="mt-1 text-sm font-medium text-slate-700">
          {hasSetlist ? `${songs.length} ${songs.length === 1 ? 'lied' : 'liederen'} · ${items.length} ${items.length === 1 ? 'onderdeel' : 'onderdelen'}` : 'Nog geen inhoud toegevoegd'}
        </p>
      </div>
      <div className="pl-6 text-sm text-slate-500 sm:pl-0">
        {openItems.length > 0 && <p className="font-medium text-amber-700">{openItems.length} onderdeel{openItems.length === 1 ? '' : 'en'} nog leeg</p>}
        {updatedAt && <p className="text-xs">Laatst gewijzigd {updatedAt}</p>}
        {!updatedAt && <p className="text-xs">Nog niet voorbereid</p>}
      </div>
      <Badge variant="secondary" className={`ml-6 w-fit sm:ml-0 ${hasSetlist ? 'text-teal-700' : 'text-slate-600'}`}>
        {hasSetlist ? 'Setlist aanwezig' : 'Nog starten'}
      </Badge>
      <ChevronRight className="hidden h-5 w-5 text-slate-300 transition group-hover:text-teal-600 sm:block" />
    </button>
  );
};

const WorshipPage = ({ section = 'dashboard' }) => {
  const { user, hasCapability } = useAuth();
  const navigate = useNavigate();
  const canManage = hasCapability('worship.manage');
  const canGrantAccess = hasCapability('worship.grant_access');
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [services, setServices] = useState([]);
  const [songs, setSongs] = useState([]);
  const [team, setTeam] = useState([]);
  const [orgTeamMembers, setOrgTeamMembers] = useState([]);
  const [orgMembers, setOrgMembers] = useState([]);
  const [addTeamMemberDialog, setAddTeamMemberDialog] = useState(false);
  const [addTeamMemberSource, setAddTeamMemberSource] = useState('team');
  const [selectedOrgUserId, setSelectedOrgUserId] = useState('');
  const [selectedOrgMemberId, setSelectedOrgMemberId] = useState('');
  const [inviteResult, setInviteResult] = useState(null);
  const [prefsDialogOpen, setPrefsDialogOpen] = useState(false);
  const [prefsMember, setPrefsMember] = useState(null);
  const [prefsForm, setPrefsForm] = useState(emptyPreferences);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [absences, setAbsences] = useState([]);
  const [absenceForm, setAbsenceForm] = useState(emptyAbsence);
  const [patterns, setPatterns] = useState([]);
  const [patternDialog, setPatternDialog] = useState(false);
  const [editingPatternId, setEditingPatternId] = useState(null);
  const [patternForm, setPatternForm] = useState(emptyPattern);
  const [generateDialog, setGenerateDialog] = useState(false);
  const [generatingPattern, setGeneratingPattern] = useState(null);
  const [generateForm, setGenerateForm] = useState(emptyGenerateForm);
  const [generateResult, setGenerateResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [rosterYear, setRosterYear] = useState(() => new Date().getFullYear());
  const [rosterMonth, setRosterMonth] = useState(() => new Date().getMonth() + 1);
  const [rosterSummary, setRosterSummary] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [rehearsals, setRehearsals] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceDialog, setServiceDialog] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [songEditorOpen, setSongEditorOpen] = useState(false);
  const [songViewerOpen, setSongViewerOpen] = useState(false);
  const [importWizardOpen, setImportWizardOpen] = useState(false);
  const [editingSong, setEditingSong] = useState(null);
  const [viewingSong, setViewingSong] = useState(null);
  const [chordProDraft, setChordProDraft] = useState(newChordPro);
  const [songIntegrations, setSongIntegrations] = useState(emptySongIntegrations);
  const [songSearch, setSongSearch] = useState('');
  const [songKeyFilter, setSongKeyFilter] = useState('');
  const [songLanguageFilter, setSongLanguageFilter] = useState('');
  const [rehearsalDialog, setRehearsalDialog] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [serviceForm, setServiceForm] = useState(emptyService);
  const [rehearsalForm, setRehearsalForm] = useState(emptyRehearsal);
  const [assignmentForm, setAssignmentForm] = useState({ user_id: '', function: 'Lead vocal', note: '' });
  const [assignmentConflicts, setAssignmentConflicts] = useState([]);
  const [assignmentOverride, setAssignmentOverride] = useState(false);
  const [assignmentOverrideReason, setAssignmentOverrideReason] = useState('');
  const [assignmentSuggestions, setAssignmentSuggestions] = useState([]);
  const [permissionSaving, setPermissionSaving] = useState(null);
  const offlineScope = user?.organization_id || user?.organizationId || 'current';
  const [planningView, setPlanningView] = useState('list');
  const [planningQuickFilter, setPlanningQuickFilter] = useState('upcoming');
  const [planningSearch, setPlanningSearch] = useState('');
  const [planningStatus, setPlanningStatus] = useState('');
  const [planningLocation, setPlanningLocation] = useState('');
  const [planningCalendarDate, setPlanningCalendarDate] = useState(() => new Date());
  const [setlistFilter, setSetlistFilter] = useState('upcoming');
  const [setlistSearch, setSetlistSearch] = useState('');
  const [setlistStatus, setSetlistStatus] = useState('');
  const [setlistLocation, setSetlistLocation] = useState('');

  const planningLocations = useMemo(
    () => [...new Set(services.map((service) => service.location).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [services],
  );
  const planningFilteredServices = useMemo(() => {
    const today = currentDateKey();
    const month = currentMonthKey();
    const search = planningSearch.trim().toLowerCase();
    return [...services]
      .filter((service) => {
        const date = serviceDateKey(service.service_date);
        if (planningQuickFilter === 'upcoming' && date < today) return false;
        if (planningQuickFilter === 'this-month' && !date.startsWith(month)) return false;
        if (planningQuickFilter === 'mine' && !service.assignments?.some((assignment) => assignment.user_id === user?.id)) return false;
        if (search && ![service.title, service.location, service.service_date, service.theme, service.speaker]
          .filter(Boolean).join(' ').toLowerCase().includes(search)) return false;
        if (planningStatus && service.status !== planningStatus) return false;
        if (planningLocation && service.location !== planningLocation) return false;
        return true;
      })
      .sort((a, b) => `${serviceDateKey(a.service_date)}${a.start_time || ''}`.localeCompare(`${serviceDateKey(b.service_date)}${b.start_time || ''}`));
  }, [planningLocation, planningQuickFilter, planningSearch, planningStatus, services, user?.id]);
  const planningMonthServices = useMemo(() => {
    const year = planningCalendarDate.getFullYear();
    const month = planningCalendarDate.getMonth();
    return planningFilteredServices.filter((service) => {
      const date = new Date(`${service.service_date}T12:00:00`);
      return date.getFullYear() === year && date.getMonth() === month;
    });
  }, [planningCalendarDate, planningFilteredServices]);
  const planningCalendarDays = useMemo(() => {
    const year = planningCalendarDate.getFullYear();
    const month = planningCalendarDate.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const mondayOffset = (first.getDay() + 6) % 7;
    const totalDays = Math.ceil((mondayOffset + last.getDate()) / 7) * 7;
    return Array.from({ length: totalDays }, (_, index) => new Date(year, month, index - mondayOffset + 1));
  }, [planningCalendarDate]);
  const planningMatrixRoles = useMemo(() => [...new Set(
    planningFilteredServices.flatMap((service) => (service.assignments || []).map((assignment) => assignment.function).filter(Boolean)),
  )].sort((a, b) => a.localeCompare(b)), [planningFilteredServices]);
  const setlistLocations = useMemo(
    () => [...new Set(services.map((service) => service.location).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [services],
  );
  const setlistFilteredServices = useMemo(() => {
    const today = currentDateKey();
    const search = setlistSearch.trim().toLowerCase();
    return [...services]
      .filter((service) => {
        const date = serviceDateKey(service.service_date);
        const { items } = getSetlistSummary(service);
        const archived = date < today || ['afgerond', 'geannuleerd'].includes(service.status);
        if (setlistFilter === 'upcoming' && (archived || service.status === 'geannuleerd')) return false;
        if (setlistFilter === 'with-setlist' && (!items.length || archived)) return false;
        if (setlistFilter === 'without-setlist' && (items.length || archived)) return false;
        if (setlistFilter === 'archive' && !archived) return false;
        if (setlistStatus && service.status !== setlistStatus) return false;
        if (setlistLocation && service.location !== setlistLocation) return false;
        if (search && ![
          service.title,
          service.location,
          service.service_date,
          ...(items || []).map((item) => item.title),
        ].filter(Boolean).join(' ').toLowerCase().includes(search)) return false;
        return true;
      })
      .sort((a, b) => `${serviceDateKey(a.service_date)}${a.start_time || ''}`.localeCompare(`${serviceDateKey(b.service_date)}${b.start_time || ''}`));
  }, [services, setlistFilter, setlistLocation, setlistStatus, setlistSearch]);

  const storeSongs = useCallback((items) => {
    setSongs(items);
    cacheWorshipData(`songs:${offlineScope}`, items).catch(() => {});
  }, [offlineScope]);

  const loadData = useCallback(async () => {
    // The same WorshipPage instance is reused when returning from a denied
    // feature route to /worship. Clear the previous feature-denied state so
    // the parent module can render again.
    setAccessDenied(false);
    setLoading(true);
    try {
      if (section === 'dashboard') {
        const [dashboardResponse, songResponse, teamResponse] = await Promise.all([
          axios.get(`${API}/worship/dashboard`),
          axios.get(`${API}/worship/songs`).catch(() => ({ data: [] })),
          axios.get(`${API}/worship/team`).catch(() => ({ data: [] })),
        ]);
        setDashboard(dashboardResponse.data);
        storeSongs(songResponse.data);
        setTeam(teamResponse.data);
      } else if (['planning', 'my-planning', 'setlists'].includes(section)) {
        const [serviceResponse, songResponse, teamResponse] = await Promise.all([
          axios.get(`${API}/worship/services`, { params: { mine: section === 'my-planning' } }),
          axios.get(`${API}/worship/songs`),
          axios.get(`${API}/worship/team`),
        ]);
        setServices(serviceResponse.data);
        storeSongs(songResponse.data);
        cacheWorshipData(`services:${offlineScope}`, serviceResponse.data).catch(() => {});
        setTeam(teamResponse.data);
      } else if (section === 'songs') {
        const response = await axios.get(`${API}/worship/songs`);
        storeSongs(response.data);
      } else if (section === 'team') {
        const response = await axios.get(`${API}/worship/team`);
        setTeam(response.data);
        if (canManage) {
          const [orgTeamResponse, orgMembersResponse] = await Promise.all([
            axios.get(`${API}/team`).catch(() => ({ data: [] })),
            axios.get(`${API}/members`).catch(() => ({ data: [] })),
          ]);
          setOrgTeamMembers(orgTeamResponse.data);
          setOrgMembers(orgMembersResponse.data);
        }
      } else if (section === 'rehearsals') {
        const [rehearsalResponse, serviceResponse, teamResponse, songResponse] = await Promise.all([
          axios.get(`${API}/worship/rehearsals`),
          axios.get(`${API}/worship/services`),
          axios.get(`${API}/worship/team`),
          axios.get(`${API}/worship/songs`),
        ]);
        setRehearsals(rehearsalResponse.data);
        setServices(serviceResponse.data);
        setTeam(teamResponse.data);
        storeSongs(songResponse.data);
      } else if (section === 'patterns') {
        const [patternResponse, teamResponse] = await Promise.all([
          axios.get(`${API}/worship/service-patterns`),
          axios.get(`${API}/worship/team`),
        ]);
        setPatterns(patternResponse.data);
        setTeam(teamResponse.data);
      } else if (section === 'roster') {
        const response = await axios.get(`${API}/worship/roster/summary`, { params: { year: rosterYear, month: rosterMonth } });
        setRosterSummary(response.data);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
      } else {
        const cachedSongs = await getCachedWorshipData(`songs:${offlineScope}`).catch(() => null);
        const cachedServices = await getCachedWorshipData(`services:${offlineScope}`).catch(() => null);
        if (cachedSongs) setSongs(cachedSongs);
        if (cachedServices) setServices(cachedServices);
        toast.warning(cachedSongs || cachedServices ? 'Offline versie geopend' : 'Worship-gegevens konden niet worden geladen');
      }
    } finally {
      setLoading(false);
    }
  }, [canManage, offlineScope, section, storeSongs, rosterYear, rosterMonth]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!canManage || !selectedService?.id || !assignmentForm.function) { setAssignmentSuggestions([]); return; }
    let cancelled = false;
    axios.get(`${API}/worship/services/${selectedService.id}/assignments/suggestions`, { params: { function: assignmentForm.function } })
      .then((res) => { if (!cancelled) setAssignmentSuggestions(res.data); })
      .catch(() => { if (!cancelled) setAssignmentSuggestions([]); });
    return () => { cancelled = true; };
  }, [canManage, selectedService?.id, assignmentForm.function]);

  const openService = async (service) => {
    try {
      const response = await axios.get(`${API}/worship/services/${service.id}`);
      setSelectedService(response.data);
      setDetailDialog(true);
    } catch (error) {
      if (service?.setlist) {
        setSelectedService(service);
        setDetailDialog(true);
        toast.warning('Offline setlist geopend');
      } else {
        toast.error('Dienst kon niet worden geopend');
      }
    }
  };

  const saveService = async (event) => {
    event.preventDefault();
    try {
      const payload = {
        ...serviceForm,
        worship_leader_id: serviceForm.worship_leader_id || null,
        zangdienst_user_id: serviceForm.zangdienst_user_id || null,
        location: serviceForm.location || null,
        theme: serviceForm.theme || null,
        speaker: serviceForm.speaker || null,
        notes: serviceForm.notes || null,
      };
      if (editingServiceId) {
        await axios.put(`${API}/worship/services/${editingServiceId}`, payload);
      } else {
        await axios.post(`${API}/worship/services`, payload);
      }
      toast.success(editingServiceId ? 'Dienst bijgewerkt' : 'Dienst aangemaakt');
      setServiceDialog(false);
      setEditingServiceId(null);
      setServiceForm(emptyService);
      if (editingServiceId) {
        setDetailDialog(false);
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dienst aanmaken mislukt');
    }
  };

  const editService = () => {
    setEditingServiceId(selectedService.id);
    setServiceForm({
      title: selectedService.title,
      service_date: selectedService.service_date,
      start_time: selectedService.start_time,
      end_time: selectedService.end_time,
      location: selectedService.location || '',
      theme: selectedService.theme || '',
      speaker: selectedService.speaker || '',
      worship_leader_id: selectedService.worship_leader_id || '',
      zangdienst_user_id: selectedService.zangdienst_user_id || '',
      status: selectedService.status,
      notes: selectedService.notes || '',
    });
    setServiceDialog(true);
  };

  const deleteService = async () => {
    if (!selectedService || !window.confirm('Deze worshipdienst definitief verwijderen?')) return;
    try {
      await axios.delete(`${API}/worship/services/${selectedService.id}`);
      toast.success('Dienst verwijderd');
      setDetailDialog(false);
      loadData();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  const refreshSelectedService = async () => {
    const response = await axios.get(`${API}/worship/services/${selectedService.id}`);
    setSelectedService(response.data);
  };

  const addAssignment = async (event) => {
    event.preventDefault();
    if (!assignmentForm.user_id) { toast.error('Kies eerst een teamlid'); return; }
    try {
      await axios.post(`${API}/worship/services/${selectedService.id}/assignments`, {
        ...assignmentForm,
        note: assignmentForm.note || null,
        override: assignmentOverride,
        override_reason: assignmentOverride ? (assignmentOverrideReason || null) : null,
      });
      setAssignmentForm({ user_id: '', function: 'Lead vocal', note: '' });
      setAssignmentConflicts([]);
      setAssignmentOverride(false);
      setAssignmentOverrideReason('');
      await refreshSelectedService();
      toast.success('Teamlid ingedeeld');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.conflicts) {
        setAssignmentConflicts(detail.conflicts);
        toast.error('Conflict gevonden — bekijk de melding hieronder');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Indelen mislukt');
      }
    }
  };

  const removeAssignment = async (assignmentId) => {
    try {
      await axios.delete(`${API}/worship/services/${selectedService.id}/assignments/${assignmentId}`);
      await refreshSelectedService();
    } catch (error) {
      toast.error('Indeling verwijderen mislukt');
    }
  };

  const respondToAssignment = async (assignment, responseStatus, availability) => {
    try {
      await axios.put(`${API}/worship/services/${selectedService.id}/assignments/${assignment.id}/response`, {
        response_status: responseStatus,
        availability,
        note: assignment.note || null,
      });
      await refreshSelectedService();
      toast.success('Beschikbaarheid bijgewerkt');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reactie opslaan mislukt');
    }
  };

  // Houdt de open dienst en dezelfde dienst in de lijsten (aantal onderdelen/liederen) gelijk na een setlistwijziging.
  const updateSetlistService = (updated) => {
    setSelectedService(updated);
    setServices((current) => current.map((service) => (service.id === updated.id ? updated : service)));
  };

  const openSetlistSong = (item) => {
    const song = songs.find((candidate) => candidate.id === item.song_id);
    if (!song) {
      toast.error('Het gekoppelde ChordPro-lied is niet beschikbaar');
      return;
    }
    openSongViewer(song, item.key);
  };

  const shareUrl = selectedService ? `${window.location.origin}/share/worship/${selectedService.id}` : '';

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link gekopieerd');
    } catch (error) {
      toast.error('Kopiëren mislukt');
    }
  };

  const openNewSong = () => {
    setEditingSong(null);
    setChordProDraft(newChordPro);
    setSongIntegrations(emptySongIntegrations);
    setSongEditorOpen(true);
  };

  const openSongEditor = (song) => {
    setEditingSong(song);
    const structuredSource = song.structured_status === 'clean' && song.structured_song
      ? serializeStructuredSong(song.structured_song)
      : null;
    setChordProDraft(structuredSource || song.chordpro || `{title: ${song.title}}\n\n${song.lyrics || ''}\n`);
    setSongIntegrations({
      songselect_id: song.songselect_id || '', publisher: song.publisher || '',
      license: song.license || '', source: song.source || '',
      google_drive_file_id: song.google_drive_file_id || '',
      google_drive_folder_id: song.google_drive_folder_id || '',
      pdf_link_url: song.pdf_link_url || '', pdf_link_label: song.pdf_link_label || '',
    });
    setSongEditorOpen(true);
  };

  const openSongViewer = (song, key = null) => {
    setViewingSong({ ...song, viewerKey: key || song.default_key });
    setSongViewerOpen(true);
  };

  const saveSong = async (event) => {
    event.preventDefault();
    const parsed = parseChordPro(chordProDraft);
    if (!parsed.validation.valid) {
      toast.error(parsed.validation.errors[0]);
      return;
    }
    try {
      const payload = buildSongPayload(chordProDraft, songIntegrations, editingSong || {});
      if (editingSong) await axios.put(`${API}/worship/songs/${editingSong.id}`, payload);
      else await axios.post(`${API}/worship/songs`, payload);
      toast.success(editingSong ? 'Lied bijgewerkt' : 'ChordPro-lied toegevoegd');
      setSongEditorOpen(false);
      setEditingSong(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Lied opslaan mislukt');
    }
  };

  const deleteSong = async (songId) => {
    if (!window.confirm('Dit lied uit de bibliotheek verwijderen?')) return;
    try {
      await axios.delete(`${API}/worship/songs/${songId}`);
      loadData();
    } catch (error) {
      toast.error('Lied verwijderen mislukt');
    }
  };

  const saveRehearsal = async (event) => {
    event.preventDefault();
    const service = services.find((item) => item.id === rehearsalForm.service_id);
    try {
      await axios.post(`${API}/worship/rehearsals`, {
        ...rehearsalForm,
        service_id: rehearsalForm.service_id || null,
        location: rehearsalForm.location || null,
        notes: rehearsalForm.notes || null,
        team_member_ids: service?.assignments?.map((item) => item.user_id) || [],
      });
      toast.success('Repetitie gepland');
      setRehearsalDialog(false);
      setRehearsalForm(emptyRehearsal);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Repetitie plannen mislukt');
    }
  };

  const toggleRehearsalSong = (songId) => {
    setRehearsalForm((current) => ({
      ...current,
      song_ids: current.song_ids.includes(songId)
        ? current.song_ids.filter((id) => id !== songId)
        : [...current.song_ids, songId],
    }));
  };

  const deleteRehearsal = async (id) => {
    if (!window.confirm('Deze repetitie verwijderen?')) return;
    try {
      await axios.delete(`${API}/worship/rehearsals/${id}`);
      loadData();
    } catch (error) {
      toast.error('Repetitie verwijderen mislukt');
    }
  };

  const serviceMap = useMemo(() => Object.fromEntries(services.map((service) => [service.id, service])), [services]);
  const songMap = useMemo(() => Object.fromEntries(songs.map((song) => [song.id, song])), [songs]);
  const filteredSongs = useMemo(() => {
    const needle = songSearch.trim().toLowerCase();
    return songs.filter((song) => {
      const searchable = [song.title, song.artist, song.composer, song.lyricist, song.lyrics, song.chordpro, song.ccli_number, song.default_key, song.bpm, song.language, ...(song.tags || [])].filter(Boolean).join(' ').toLowerCase();
      return (!needle || searchable.includes(needle))
        && (!songKeyFilter || song.default_key === songKeyFilter)
        && (!songLanguageFilter || song.language === songLanguageFilter);
    });
  }, [songKeyFilter, songLanguageFilter, songSearch, songs]);
  const songLanguages = useMemo(() => [...new Set(songs.map((song) => song.language).filter(Boolean))].sort(), [songs]);
  const draftDocument = useMemo(() => parseChordPro(chordProDraft), [chordProDraft]);

  const renderDashboard = () => {
    const next = dashboard?.next_service;
    return (
      <div className="space-y-6">
        <SectionHeader title="Worship Dashboard" description="Je eerstvolgende diensten, repetities en openstaande acties." />
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="border-0 shadow-sm md:col-span-2">
            <CardHeader><CardTitle className="text-base">Eerstvolgende dienst</CardTitle></CardHeader>
            <CardContent>
              {next ? <ServiceCard service={next} onOpen={openService} /> : <EmptyState icon={CalendarDays} title="Geen dienst gepland" text="Er staat nog geen toekomstige worshipdienst klaar." />}
            </CardContent>
          </Card>
          <div className="grid gap-4">
            <Card className="border-0 shadow-sm"><CardContent className="p-5"><p className="text-sm text-slate-500">Openstaande bevestigingen</p><p className="mt-2 text-3xl font-bold text-slate-900">{dashboard?.pending_confirmations || 0}</p></CardContent></Card>
            <Card className="border-0 shadow-sm"><CardContent className="p-5"><p className="text-sm text-slate-500">Aankomende repetitie</p><p className="mt-2 font-semibold text-slate-900">{dashboard?.next_rehearsal ? formatDate(dashboard.next_rehearsal.rehearsal_date) : 'Niet gepland'}</p>{dashboard?.next_rehearsal && <p className="text-sm text-slate-500">{dashboard.next_rehearsal.start_time} · {dashboard.next_rehearsal.location || 'Locatie volgt'}</p>}</CardContent></Card>
          </div>
        </div>
        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle className="text-base">Komende planning</CardTitle></CardHeader>
          <CardContent className="grid gap-3 lg:grid-cols-2">
            {(dashboard?.upcoming_services || []).slice(1).map((service) => <ServiceCard key={service.id} service={service} onOpen={openService} />)}
            {(dashboard?.upcoming_services || []).length <= 1 && <EmptyState icon={Music2} title="Geen extra diensten" text="Nieuwe diensten verschijnen hier automatisch." />}
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle className="text-base">Notificaties</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(dashboard?.notifications || []).map((notification) => (
              <div key={notification.id} className="flex items-start gap-3 rounded-md border border-slate-100 p-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-50 text-teal-700"><Music2 className="h-4 w-4" /></div>
                <div><p className="text-sm font-medium text-slate-800">{notification.title}</p><p className="text-xs text-slate-500">In-app notificatie</p></div>
              </div>
            ))}
            {!(dashboard?.notifications || []).length && <p className="text-sm text-slate-500">Geen nieuwe worshipnotificaties.</p>}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderPlanning = (mine = false) => {
    if (section === 'setlists') {
      const groupedSetlists = setlistFilteredServices.reduce((groups, service) => {
        const key = service.service_date?.slice(0, 7) || 'onbekend';
        groups[key] = [...(groups[key] || []), service];
        return groups;
      }, {});
      const hasSetlistFilters = setlistFilter !== 'upcoming' || setlistSearch || setlistStatus || setlistLocation;
      const clearSetlistFilters = () => {
        setSetlistFilter('upcoming');
        setSetlistSearch('');
        setSetlistStatus('');
        setSetlistLocation('');
      };
      return (
        <div className="space-y-5">
          <SectionHeader
            title="Setlists"
            description="Bereid de inhoud en volgorde van komende diensten voor."
          />
          <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
            <div className="-mx-1 flex flex-nowrap gap-2 overflow-x-auto px-1 pb-1">
              {[['upcoming', 'Komend'], ['with-setlist', 'Setlist aanwezig'], ['without-setlist', 'Nog starten'], ['archive', 'Archief']].map(([value, label]) => (
                <button key={value} type="button" onClick={() => setSetlistFilter(value)} className={`min-h-9 rounded-full border px-3 text-sm font-medium transition ${setlistFilter === value ? 'border-teal-200 bg-teal-50 text-teal-800' : 'border-slate-200 text-slate-600 hover:border-teal-200 hover:bg-slate-50'}`} aria-pressed={setlistFilter === value}>{label}</button>
              ))}
            </div>
            <div className="grid gap-2 border-t border-slate-100 pt-3 sm:grid-cols-[minmax(0,1fr)_minmax(150px,190px)_minmax(150px,190px)_auto]">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input aria-label="Zoek setlists" className="pl-9" placeholder="Zoek dienst, locatie of lied" value={setlistSearch} onChange={(event) => setSetlistSearch(event.target.value)} />
              </label>
              <select aria-label="Filter op status" className="h-10 min-w-0 rounded-md border border-slate-200 bg-white px-3 text-sm" value={setlistStatus} onChange={(event) => setSetlistStatus(event.target.value)}>
                <option value="">Alle statussen</option>
                {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
              <select aria-label="Filter op locatie" className="h-10 min-w-0 rounded-md border border-slate-200 bg-white px-3 text-sm" value={setlistLocation} onChange={(event) => setSetlistLocation(event.target.value)}>
                <option value="">Alle locaties</option>
                {setlistLocations.map((location) => <option key={location} value={location}>{location}</option>)}
              </select>
              {hasSetlistFilters ? <Button type="button" variant="outline" onClick={clearSetlistFilters} className="h-10">Filters wissen</Button> : <div className="hidden md:block" />}
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 text-sm text-slate-500">
            <span>{setlistFilteredServices.length} {setlistFilteredServices.length === 1 ? 'resultaat' : 'resultaten'}</span>
          </div>
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            {Object.keys(groupedSetlists).length ? Object.entries(groupedSetlists).map(([month, monthServices]) => (
              <section key={month}>
                <div className="border-b border-slate-200 bg-slate-50/80 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 sm:px-5">{month === 'onbekend' ? 'Zonder datum' : formatMonthHeading(`${month}-01`)}</div>
                {monthServices.map((service) => <CompactSetlistRow key={service.id} service={service} onOpen={openService} />)}
              </section>
            )) : (
              <div className="p-8">
                <EmptyState
                  icon={setlistFilter === 'archive' ? Files : Music2}
                  title={setlistFilter === 'archive' ? 'Geen historische setlists' : hasSetlistFilters ? 'Geen setlists gevonden' : 'Geen komende setlists'}
                  text={hasSetlistFilters ? 'Pas je filters aan of wis ze om andere diensten te zien.' : 'Er zijn momenteel geen toekomstige diensten om voor te bereiden.'}
                />
              </div>
            )}
          </div>
        </div>
      );
    }
    if (section !== 'planning') {
      return (
        <div className="space-y-6">
          <SectionHeader
            title={mine ? 'Mijn planning' : 'Setlists'}
            description={mine ? 'Bekijk je indelingen en geef je beschikbaarheid door.' : 'Open een dienst om de volgorde en liederen te beheren.'}
            action={canManage && <Button onClick={() => setServiceDialog(true)} className="bg-teal-600 text-white hover:bg-teal-700"><Plus className="mr-2 h-4 w-4" />Nieuwe dienst</Button>}
          />
          <div className="grid gap-4 xl:grid-cols-2">
            {services.map((service) => <ServiceCard key={service.id} service={service} onOpen={openService} ownAssignment={mine ? service.assignments?.find((item) => item.user_id === user.id) : null} />)}
          </div>
          {!services.length && <Card className="border-0 shadow-sm"><CardContent><EmptyState icon={CalendarDays} title="Geen diensten gevonden" text={mine ? 'Je bent nog niet ingedeeld.' : 'Maak de eerste worshipdienst aan.'} /></CardContent></Card>}
        </div>
      );
    }

    const groupedServices = planningFilteredServices.reduce((groups, service) => {
      const key = service.service_date?.slice(0, 7) || 'onbekend';
      groups[key] = [...(groups[key] || []), service];
      return groups;
    }, {});
    const hasFilters = planningQuickFilter !== 'upcoming' || planningSearch || planningStatus || planningLocation;
    const clearFilters = () => {
      setPlanningQuickFilter('upcoming');
      setPlanningSearch('');
      setPlanningStatus('');
      setPlanningLocation('');
    };
    const monthStart = new Date(planningCalendarDate.getFullYear(), planningCalendarDate.getMonth(), 1);
    const monthTitle = new Intl.DateTimeFormat('nl-NL', { month: 'long', year: 'numeric' }).format(monthStart);
    const previousMonth = () => setPlanningCalendarDate(new Date(planningCalendarDate.getFullYear(), planningCalendarDate.getMonth() - 1, 1));
    const nextMonth = () => setPlanningCalendarDate(new Date(planningCalendarDate.getFullYear(), planningCalendarDate.getMonth() + 1, 1));
    const goToday = () => setPlanningCalendarDate(new Date());

    return (
      <div className="space-y-5">
        <SectionHeader
          title="Planning"
          description="Plan diensten, teamleden en de inhoud van samenkomsten."
          action={canManage && <Button onClick={() => setServiceDialog(true)} className="bg-teal-600 text-white hover:bg-teal-700"><Plus className="mr-2 h-4 w-4" />Nieuwe dienst</Button>}
        />

        <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" aria-label="Planningweergave">
              {[['list', 'Lijst', List], ['calendar', 'Kalender', CalendarDays], ['matrix', 'Matrix', LayoutGrid]].map(([value, label, Icon]) => (
                <button key={value} type="button" onClick={() => setPlanningView(value)} className={`inline-flex min-h-9 items-center gap-2 rounded-md px-3 text-sm font-medium transition ${planningView === value ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`} aria-pressed={planningView === value}>
                  <Icon className="h-4 w-4" />{label}
                </button>
              ))}
            </div>
            <p className="text-sm text-slate-500">{planningFilteredServices.length} {planningFilteredServices.length === 1 ? 'dienst' : 'diensten'}</p>
          </div>
          <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
            {[['upcoming', 'Komend'], ['this-month', 'Deze maand'], ['mine', 'Mijn diensten'], ['all', 'Alle']].map(([value, label]) => (
              <button key={value} type="button" onClick={() => setPlanningQuickFilter(value)} className={`min-h-9 rounded-full border px-3 text-sm font-medium transition ${planningQuickFilter === value ? 'border-teal-200 bg-teal-50 text-teal-800' : 'border-slate-200 text-slate-600 hover:border-teal-200 hover:bg-slate-50'}`} aria-pressed={planningQuickFilter === value}>{label}</button>
            ))}
          </div>
          <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_170px_190px_auto]">
            <label className="relative block">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input aria-label="Zoek diensten" className="pl-9" placeholder="Zoek titel, locatie of datum" value={planningSearch} onChange={(event) => setPlanningSearch(event.target.value)} />
            </label>
            <select aria-label="Filter op status" className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={planningStatus} onChange={(event) => setPlanningStatus(event.target.value)}>
              <option value="">Alle statussen</option>
              {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <select aria-label="Filter op locatie" className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={planningLocation} onChange={(event) => setPlanningLocation(event.target.value)}>
              <option value="">Alle locaties</option>
              {planningLocations.map((location) => <option key={location} value={location}>{location}</option>)}
            </select>
            {hasFilters ? <Button type="button" variant="outline" onClick={clearFilters} className="h-10">Filters wissen</Button> : <div className="hidden md:block" />}
          </div>
        </div>

        {planningView === 'list' && (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            {Object.keys(groupedServices).length ? Object.entries(groupedServices).map(([month, monthServices]) => (
              <section key={month}>
                <div className="border-b border-slate-200 bg-slate-50/80 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 sm:px-5">{month === 'onbekend' ? 'Zonder datum' : formatMonthHeading(`${month}-01`)}</div>
                {monthServices.map((service) => <CompactServiceRow key={service.id} service={service} onOpen={openService} ownAssignment={service.assignments?.find((item) => item.user_id === user?.id)} />)}
              </section>
            )) : <div className="p-8"><EmptyState icon={hasFilters ? Search : CalendarDays} title={hasFilters ? 'Geen diensten gevonden' : 'Geen komende diensten'} text={hasFilters ? 'Pas je filters aan of wis ze om alle beschikbare diensten te zien.' : 'Er staan momenteel geen diensten gepland.'} /></div>}
          </div>
        )}

        {planningView === 'calendar' && (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 sm:px-5">
              <div className="flex items-center gap-2"><Button variant="outline" size="icon" onClick={previousMonth} aria-label="Vorige maand"><ChevronLeft className="h-4 w-4" /></Button><h2 className="min-w-40 text-center font-semibold capitalize text-slate-900">{monthTitle}</h2><Button variant="outline" size="icon" onClick={nextMonth} aria-label="Volgende maand"><ChevronRight className="h-4 w-4" /></Button></div>
              <Button variant="outline" size="sm" onClick={goToday}>Vandaag</Button>
            </div>
            <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50/70 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">{['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map((day) => <div key={day} className="py-2">{day}</div>)}</div>
            <div className="grid grid-cols-7">
              {planningCalendarDays.map((day) => {
                const dayKey = localDateKey(day);
                const dayServices = planningMonthServices.filter((service) => service.service_date === dayKey);
                const inMonth = day.getMonth() === planningCalendarDate.getMonth();
                return <div key={dayKey} className={`min-h-28 border-b border-r border-slate-100 p-1.5 sm:min-h-32 sm:p-2 ${inMonth ? 'bg-white' : 'bg-slate-50/60'}`}>
                  <div className={`mb-1 flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${dayKey === currentDateKey() ? 'bg-teal-600 text-white' : inMonth ? 'text-slate-700' : 'text-slate-300'}`}>{day.getDate()}</div>
                  <div className="space-y-1">{dayServices.slice(0, 3).map((service) => <button key={service.id} type="button" onClick={() => openService(service)} className="block w-full truncate rounded bg-teal-50 px-1.5 py-1 text-left text-[11px] font-medium text-teal-800 hover:bg-teal-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"><span className="font-semibold">{service.start_time}</span> {service.title}</button>)}{dayServices.length > 3 && <p className="px-1 text-[11px] font-medium text-slate-500">+{dayServices.length - 3} meer</p>}</div>
                </div>;
              })}
            </div>
          </div>
        )}

        {planningView === 'matrix' && (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 sm:px-5"><div><h2 className="font-semibold text-slate-900">Bezettingsmatrix</h2><p className="text-sm text-slate-500">Leesoverzicht van de geselecteerde diensten en bestaande teamindeling.</p></div><span className="text-sm text-slate-500">{Math.min(planningFilteredServices.length, 8)} van {planningFilteredServices.length}</span></div>
            {planningMatrixRoles.length && planningFilteredServices.length ? <div className="overflow-x-auto"><table className="min-w-[760px] w-full border-collapse text-sm"><thead><tr className="border-b border-slate-200 bg-slate-50/70 text-left"><th className="sticky left-0 z-10 min-w-44 bg-slate-50/70 px-4 py-3 font-semibold text-slate-600">Functie</th>{planningFilteredServices.slice(0, 8).map((service) => <th key={service.id} className="min-w-36 px-3 py-3 align-top"><button type="button" onClick={() => openService(service)} className="text-left hover:text-teal-700"><span className="block font-semibold text-slate-800">{formatCompactServiceDate(service.service_date)}</span><span className="mt-0.5 block truncate text-xs font-normal text-slate-500">{service.title}</span></button></th>)}</tr></thead><tbody>{planningMatrixRoles.map((role) => <tr key={role} className="border-b border-slate-100 last:border-0"><th className="sticky left-0 bg-white px-4 py-3 text-left font-medium text-slate-700">{role}</th>{planningFilteredServices.slice(0, 8).map((service) => { const assigned = service.assignments?.filter((assignment) => assignment.function === role) || []; return <td key={service.id} className="px-3 py-3 align-top text-slate-600">{assigned.length ? assigned.map((assignment) => <div key={assignment.id || assignment.user_id} className="truncate">{assignment.name || 'Ingepland'}</div>) : <span className="text-slate-300">—</span>}</td>; })}</tr>)}</tbody></table></div> : <div className="p-8"><EmptyState icon={LayoutGrid} title="Nog geen bezetting om te vergelijken" text="De matrix toont functies zodra er teamleden aan diensten zijn gekoppeld." /></div>}
          </div>
        )}
      </div>
    );
  };

  const renderSongs = () => (
    <div className="space-y-6">
      <SectionHeader title="Liederen" description="ChordPro-bibliotheek voor muzikanten, beamer, print en setlists." action={canManage && <div className="flex flex-wrap gap-2"><Button variant="outline" onClick={() => setImportWizardOpen(true)}><FileUp className="mr-2 h-4 w-4" />Importeer liedbestand</Button><Button onClick={openNewSong} className="bg-teal-600 text-white hover:bg-teal-700"><Plus className="mr-2 h-4 w-4" />Nieuw ChordPro-lied</Button></div>} />
      <div className="grid gap-3 rounded-md border border-slate-200 bg-white p-4 md:grid-cols-[1fr_180px_180px]">
        <Input aria-label="Zoek liederen" placeholder="Zoek op titel, artiest, tekst, CCLI of tags" value={songSearch} onChange={(event) => setSongSearch(event.target.value)} />
        <select className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={songKeyFilter} onChange={(event) => setSongKeyFilter(event.target.value)}><option value="">Alle toonsoorten</option>{[...new Set(songs.map((song) => song.default_key).filter(Boolean))].sort().map((key) => <option key={key}>{key}</option>)}</select>
        <select className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={songLanguageFilter} onChange={(event) => setSongLanguageFilter(event.target.value)}><option value="">Alle talen</option>{songLanguages.map((language) => <option key={language}>{language}</option>)}</select>
      </div>
      <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
        <div className="hidden grid-cols-[2fr_1.5fr_0.6fr_0.6fr_1fr_auto] gap-4 border-b border-slate-200 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase text-slate-500 md:grid">
          <span>Titel</span><span>Artiest</span><span>Toonsoort</span><span>BPM</span><span>Tags</span><span />
        </div>
        {filteredSongs.map((song) => (
          <div key={song.id} role="button" tabIndex={0} onClick={() => openSongViewer(song)} onKeyDown={(event) => event.key === 'Enter' && openSongViewer(song)} className="grid cursor-pointer gap-2 border-b border-slate-100 px-5 py-4 last:border-0 hover:bg-slate-50 md:grid-cols-[2fr_1.5fr_0.6fr_0.6fr_1fr_auto] md:items-center md:gap-4">
            <div><p className="font-medium text-slate-900">{song.title}</p><p className="text-xs text-slate-500 md:hidden">{song.artist || 'Onbekende artiest'}</p></div>
            <span className="hidden text-sm text-slate-600 md:block">{song.artist || '-'}</span>
            <span className="text-sm text-slate-600">{song.default_key || '-'}</span>
            <span className="text-sm text-slate-600">{song.bpm || '-'}</span>
            <div className="flex flex-wrap gap-1">{song.tags?.slice(0, 2).map((tag) => <Badge key={tag} variant="secondary">{tag}</Badge>)}</div>
            <div className="flex gap-1"><Button variant="ghost" size="icon" title="Open lied" onClick={(event) => { event.stopPropagation(); openSongViewer(song); }}><Eye className="h-4 w-4" /></Button>{canManage && <><Button variant="ghost" size="icon" title="ChordPro bewerken" onClick={(event) => { event.stopPropagation(); openSongEditor(song); }}><Pencil className="h-4 w-4" /></Button><Button variant="ghost" size="icon" title="Lied verwijderen" onClick={(event) => { event.stopPropagation(); deleteSong(song.id); }}><Trash2 className="h-4 w-4 text-red-500" /></Button></>}</div>
          </div>
        ))}
        {!filteredSongs.length && <EmptyState icon={Music2} title="Geen liederen gevonden" text="Pas de filters aan of importeer een ChordPro-bestand." />}
      </div>
    </div>
  );

  const availableOrgTeamMembers = orgTeamMembers.filter(
    (member) => !team.some((worshipMember) => worshipMember.id === member.id),
  );
  const availableOrgMembers = orgMembers.filter(
    (member) => !team.some((worshipMember) => worshipMember.source_member_id === member.id),
  );

  const openAddTeamMemberDialog = () => {
    setAddTeamMemberSource('team');
    setSelectedOrgUserId('');
    setSelectedOrgMemberId('');
    setInviteResult(null);
    setAddTeamMemberDialog(true);
  };

  const addExistingTeamMember = async (event) => {
    event.preventDefault();
    if (!selectedOrgUserId) {
      toast.error('Kies een teamlid');
      return;
    }
    try {
      await axios.post(`${API}/worship/team/members`, { user_id: selectedOrgUserId });
      toast.success('Teamlid toegevoegd aan worshipteam');
      setAddTeamMemberDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Toevoegen mislukt');
    }
  };

  const inviteOrgMember = async (event) => {
    event.preventDefault();
    if (!selectedOrgMemberId) {
      toast.error('Kies een gemeentelid');
      return;
    }
    try {
      const response = await axios.post(`${API}/worship/team/members/from-member`, { member_id: selectedOrgMemberId });
      toast.success('Uitnodiging verstuurd');
      setInviteResult(response.data.invite_link);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Uitnodigen mislukt');
    }
  };

  const removeWorshipTeamMember = async (membershipId) => {
    if (!membershipId || !window.confirm('Dit teamlid uit het worshipteam verwijderen?')) return;
    try {
      await axios.delete(`${API}/worship/team/members/${membershipId}`);
      toast.success('Teamlid verwijderd');
      loadData();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  const updateWorshipPermission = async (member, permission, effect) => {
    setPermissionSaving(`${member.id}:${permission}`);
    try {
      const response = await axios.put(`${API}/worship/team/permissions/${member.id}`, {
        permission,
        effect,
      });
      setTeam((items) => items.map((item) => item.id === member.id ? { ...item, ...response.data } : item));
      toast.success(effect === 'inherit' ? 'Worshiprecht erft weer van de rol' : 'Worshiprecht bijgewerkt');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Worshiprecht wijzigen mislukt');
    } finally {
      setPermissionSaving(null);
    }
  };

  const openPreferencesDialog = async (member) => {
    setPrefsMember(member);
    setPrefsForm(emptyPreferences);
    setAbsenceForm(emptyAbsence);
    setAbsences([]);
    setPrefsDialogOpen(true);
    try {
      const [prefsRes, absencesRes] = await Promise.all([
        axios.get(`${API}/worship/members/${member.id}/preferences`),
        axios.get(`${API}/members/${member.id}/absences`),
      ]);
      if (prefsRes.data) {
        setPrefsForm({
          target_per_month: prefsRes.data.target_per_month != null ? String(prefsRes.data.target_per_month) : '',
          min_per_month: prefsRes.data.min_per_month != null ? String(prefsRes.data.min_per_month) : '',
          max_per_month: prefsRes.data.max_per_month != null ? String(prefsRes.data.max_per_month) : '',
          eligible_functions: prefsRes.data.eligible_functions || [],
          unavailable_weekdays: prefsRes.data.unavailable_weekdays || [],
          preferred_service_time: prefsRes.data.preferred_service_time || '',
          notes: prefsRes.data.notes || '',
        });
      }
      setAbsences(absencesRes.data || []);
    } catch (error) {
      toast.error('Kon voorkeuren niet laden');
    }
  };

  const togglePrefsFunction = (fn) => {
    setPrefsForm((prev) => ({
      ...prev,
      eligible_functions: prev.eligible_functions.includes(fn)
        ? prev.eligible_functions.filter((f) => f !== fn)
        : [...prev.eligible_functions, fn],
    }));
  };

  const togglePrefsWeekday = (day) => {
    setPrefsForm((prev) => ({
      ...prev,
      unavailable_weekdays: prev.unavailable_weekdays.includes(day)
        ? prev.unavailable_weekdays.filter((d) => d !== day)
        : [...prev.unavailable_weekdays, day],
    }));
  };

  const savePreferences = async () => {
    if (!prefsMember) return;
    setPrefsSaving(true);
    try {
      const payload = {
        target_per_month: prefsForm.target_per_month ? parseInt(prefsForm.target_per_month, 10) : null,
        min_per_month: prefsForm.min_per_month ? parseInt(prefsForm.min_per_month, 10) : null,
        max_per_month: prefsForm.max_per_month ? parseInt(prefsForm.max_per_month, 10) : null,
        eligible_functions: prefsForm.eligible_functions,
        unavailable_weekdays: prefsForm.unavailable_weekdays,
        preferred_service_time: prefsForm.preferred_service_time || null,
        notes: prefsForm.notes || null,
      };
      await axios.put(`${API}/worship/members/${prefsMember.id}/preferences`, payload);
      toast.success('Voorkeuren opgeslagen');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setPrefsSaving(false);
    }
  };

  const addAbsence = async () => {
    if (!prefsMember || !absenceForm.start_date || !absenceForm.end_date) {
      toast.error('Vul een start- en einddatum in');
      return;
    }
    try {
      const res = await axios.post(`${API}/members/${prefsMember.id}/absences`, absenceForm);
      setAbsences((prev) => [...prev, res.data].sort((a, b) => a.start_date.localeCompare(b.start_date)));
      setAbsenceForm(emptyAbsence);
      toast.success('Afwezigheid toegevoegd');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Toevoegen mislukt');
    }
  };

  const deleteAbsence = async (absenceId) => {
    if (!prefsMember) return;
    try {
      await axios.delete(`${API}/members/${prefsMember.id}/absences/${absenceId}`);
      setAbsences((prev) => prev.filter((a) => a.id !== absenceId));
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  const openPatternDialog = (pattern) => {
    if (pattern) {
      setEditingPatternId(pattern.id);
      setPatternForm({
        title: pattern.title,
        weekday: pattern.weekday,
        start_time: pattern.start_time,
        end_time: pattern.end_time,
        location: pattern.location || '',
        default_worship_leader_id: pattern.default_worship_leader_id || '',
        is_active: pattern.is_active,
      });
    } else {
      setEditingPatternId(null);
      setPatternForm(emptyPattern);
    }
    setPatternDialog(true);
  };

  const savePattern = async (event) => {
    event.preventDefault();
    try {
      const payload = {
        ...patternForm,
        weekday: Number(patternForm.weekday),
        location: patternForm.location || null,
        default_worship_leader_id: patternForm.default_worship_leader_id || null,
      };
      if (editingPatternId) {
        await axios.put(`${API}/worship/service-patterns/${editingPatternId}`, payload);
      } else {
        await axios.post(`${API}/worship/service-patterns`, payload);
      }
      toast.success(editingPatternId ? 'Patroon bijgewerkt' : 'Patroon aangemaakt');
      setPatternDialog(false);
      setEditingPatternId(null);
      setPatternForm(emptyPattern);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Opslaan mislukt');
    }
  };

  const deletePattern = async (patternId) => {
    if (!window.confirm('Dit dienstpatroon definitief verwijderen?')) return;
    try {
      await axios.delete(`${API}/worship/service-patterns/${patternId}`);
      toast.success('Patroon verwijderd');
      loadData();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  const openGenerateDialog = (pattern) => {
    setGeneratingPattern(pattern);
    setGenerateForm(emptyGenerateForm);
    setGenerateResult(null);
    setGenerateDialog(true);
  };

  const submitGenerate = async (event) => {
    event.preventDefault();
    setGenerating(true);
    setGenerateResult(null);
    try {
      const skipDates = generateForm.skip_dates
        .split(',')
        .map((d) => d.trim())
        .filter(Boolean);
      const response = await axios.post(
        `${API}/worship/service-patterns/${generatingPattern.id}/generate`,
        { year: Number(generateForm.year), month: Number(generateForm.month), skip_dates: skipDates },
      );
      setGenerateResult(response.data);
      if (response.data.created.length) {
        toast.success(`${response.data.created.length} dienst(en) aangemaakt als concept`);
      } else {
        toast.warning('Geen nieuwe diensten aangemaakt — alle datums bestonden al');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Genereren mislukt');
    } finally {
      setGenerating(false);
    }
  };

  const shiftRosterMonth = (delta) => {
    const next = shiftRosterMonthValue(rosterYear, rosterMonth, delta);
    setRosterYear(next.year);
    setRosterMonth(next.month);
  };

  const publishRoster = async () => {
    if (!window.confirm(`Rooster voor ${monthLabel(rosterYear, rosterMonth)} versturen? Eventuele conceptdiensten worden definitief, en elk betrokken teamlid ontvangt een e-mail met hun planning deze maand (mensen die 'm al eerder kregen ontvangen 'm niet opnieuw).`)) return;
    setPublishing(true);
    try {
      const response = await axios.post(`${API}/worship/roster/${rosterYear}/${rosterMonth}/publish`);
      toast.success(`${response.data.published_services} dienst(en) gepubliceerd · ${response.data.recipients_notified} teamlid(leden) genotificeerd`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Publiceren mislukt');
    } finally {
      setPublishing(false);
    }
  };

  const renderTeam = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Worshipteam"
        description="Gebruikers die beschikbaar zijn voor worshipplanning."
        action={canManage && <Button onClick={openAddTeamMemberDialog} className="bg-teal-600 text-white hover:bg-teal-700"><Plus className="mr-2 h-4 w-4" />Teamlid toevoegen</Button>}
      />
      <div className="grid gap-4 xl:grid-cols-2 2xl:grid-cols-3">
        {team.map((member) => (
          <Card key={member.id} className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-teal-100 font-semibold text-teal-800">{member.name.charAt(0)}</div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-slate-900">{member.name}</p>
                  <p className="truncate text-sm text-slate-500">{member.email}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(member.roles || []).map((r) => <Badge key={r} variant="secondary">{worshipRoleLabel(r)}</Badge>)}
                    {member.source_member_id && <Badge variant="outline">Gemeentelid</Badge>}
                  </div>
                </div>
                {canManage && (
                  <div className="flex shrink-0 items-center gap-1">
                    <Button variant="ghost" size="icon" title="Voorkeuren" onClick={() => openPreferencesDialog(member)}>
                      <SlidersHorizontal className="h-4 w-4 text-slate-500" />
                    </Button>
                    {member.membership_id && (
                      <Button variant="ghost" size="icon" title="Uit worshipteam verwijderen" onClick={() => removeWorshipTeamMember(member.membership_id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {canGrantAccess ? (
                <div className="mt-4 border-t border-slate-100 pt-4 text-xs text-slate-600">
                  <p className="font-medium text-slate-700">Toegang binnen Worship</p>
                  <p className="mt-1 text-slate-500">Wijs hier persoonlijke uitzonderingen toe. Rollen geven daarnaast standaardtoegang.</p>
                  <div className="mt-3">
                    <WorshipPermissionControls
                      member={member}
                      savingPermission={permissionSaving?.startsWith(`${member.id}:`)
                        ? permissionSaving.split(':').slice(1).join(':')
                        : null}
                      onChange={(permission, effect) => updateWorshipPermission(member, permission, effect)}
                    />
                  </div>
                </div>
              ) : (
                <div className="mt-4 border-t border-slate-100 pt-4 text-xs text-slate-500">
                  Toegang wordt beheerd door de Worshipleader of beheerder.
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {!team.length && <EmptyState icon={UserCheck} title="Nog geen worshipteam samengesteld" text="Voeg teamleden of gemeenteleden toe om te beginnen." />}
      </div>
    </div>
  );

  const renderRehearsals = () => (
    <div className="space-y-6">
      <SectionHeader title="Repetities" description="Plan oefenmomenten en koppel ze aan een dienst." action={canManage && <Button onClick={() => setRehearsalDialog(true)} className="bg-teal-600 text-white hover:bg-teal-700"><Plus className="mr-2 h-4 w-4" />Repetitie plannen</Button>} />
      <div className="grid gap-4 lg:grid-cols-2">
        {rehearsals.map((rehearsal) => (
          <Card key={rehearsal.id} className="border-0 shadow-sm"><CardContent className="p-5"><div className="flex items-start justify-between gap-3"><div className="min-w-0"><p className="font-semibold text-slate-900">{formatDate(rehearsal.rehearsal_date)}</p><p className="mt-1 text-sm text-slate-500">{rehearsal.start_time} - {rehearsal.end_time} · {rehearsal.location || 'Locatie volgt'}</p>{rehearsal.service_id && <p className="mt-2 text-sm font-medium text-teal-700">{serviceMap[rehearsal.service_id]?.title || 'Gekoppelde dienst'}</p>}<p className="mt-2 text-xs text-slate-500">{rehearsal.team_member_ids?.length || 0} teamleden</p>{!!rehearsal.song_ids?.length && <div className="mt-2 flex flex-wrap gap-1">{rehearsal.song_ids.map((songId) => <Badge key={songId} variant="secondary">{songMap[songId]?.title || 'Lied'}</Badge>)}</div>}</div>{canManage && <Button variant="ghost" size="icon" onClick={() => deleteRehearsal(rehearsal.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>}</div></CardContent></Card>
        ))}
      </div>
      {!rehearsals.length && <Card className="border-0 shadow-sm"><CardContent><EmptyState icon={CalendarDays} title="Geen repetities gepland" text="Plan een repetitie zodra de volgende dienst bekend is." /></CardContent></Card>}
    </div>
  );

  const renderRoster = () => {
    return (
      <WorshipRosterSection
        year={rosterYear}
        month={rosterMonth}
        summary={rosterSummary}
        canManage={canManage}
        publishing={publishing}
        onPreviousMonth={() => shiftRosterMonth(-1)}
        onNextMonth={() => shiftRosterMonth(1)}
        onToday={() => {
          const today = new Date();
          setRosterYear(today.getFullYear());
          setRosterMonth(today.getMonth() + 1);
        }}
        onOpenService={openService}
        onPublish={publishRoster}
      />
    );
  };

  const renderPatterns = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Terugkerende dienstpatronen"
        description="Definieer terugkerende diensten (bijv. elke zondag) en genereer in één keer een hele maand als concept."
        action={canManage && <Button onClick={() => openPatternDialog(null)} className="bg-teal-600 text-white hover:bg-teal-700"><Plus className="mr-2 h-4 w-4" />Patroon toevoegen</Button>}
      />
      <div className="grid gap-4 lg:grid-cols-2">
        {patterns.map((pattern) => (
          <Card key={pattern.id} className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900">{pattern.title}</p>
                  <p className="mt-1 text-sm text-slate-500">
                    Elke {WEEKDAYS.find((w) => w.value === pattern.weekday)?.label || pattern.weekday} · {pattern.start_time} - {pattern.end_time}
                  </p>
                  {pattern.location && <p className="mt-1 text-sm text-slate-500">{pattern.location}</p>}
                  {pattern.default_worship_leader_id && (
                    <p className="mt-1 text-sm text-slate-500">Worshipleader: {team.find((m) => m.id === pattern.default_worship_leader_id)?.name || '-'}</p>
                  )}
                  {!pattern.is_active && <Badge variant="secondary" className="mt-2">Inactief</Badge>}
                </div>
                {canManage && (
                  <div className="flex shrink-0 gap-1">
                    <Button variant="ghost" size="icon" title="Bewerken" onClick={() => openPatternDialog(pattern)}><Pencil className="h-4 w-4 text-slate-500" /></Button>
                    <Button variant="ghost" size="icon" title="Verwijderen" onClick={() => deletePattern(pattern.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                )}
              </div>
              {canManage && (
                <Button variant="outline" size="sm" className="mt-4" onClick={() => openGenerateDialog(pattern)}>
                  <CalendarDays className="mr-2 h-4 w-4" />Genereer maandrooster
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
        {!patterns.length && (
          <Card className="border-0 shadow-sm lg:col-span-2">
            <CardContent>
              <EmptyState icon={Repeat} title="Nog geen dienstpatronen" text="Maak een patroon aan, bijvoorbeeld 'elke zondag 10:00-11:30', om snel een hele maand aan diensten te genereren." />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );

  const renderFiles = () => (
    <div className="space-y-6">
      <SectionHeader title="Bestanden" description="Bladmuziek, akkoordenschema’s, demo’s en planningsdocumenten." />
      <Card className="border-0 shadow-sm"><CardContent><EmptyState icon={Files} title="Bestandsopslag volgt" text="De huidige beveiligde upload is uitsluitend voor ledendocumenten. Worship-bestanden krijgen een eigen afgeschermde opslag in een volgende versie." /></CardContent></Card>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <SectionHeader title="Worship-instellingen" description="Rollen, rechten en toekomstige integraties." />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="border-0 shadow-sm"><CardHeader><CardTitle className="flex items-center gap-2 text-base"><UserCheck className="h-5 w-5 text-teal-700" />Jouw toegang</CardTitle></CardHeader><CardContent><div className="flex flex-wrap gap-2">{(user?.roles || []).map((r) => <Badge key={r}>{r}</Badge>)}</div><p className="mt-3 text-sm text-slate-600">{canManage ? 'Je kunt diensten, indelingen, setlists, liederen en repetities beheren.' : 'Je ziet alleen je eigen planning en gekoppelde worshipinformatie.'}</p></CardContent></Card>
        <Card className="border-0 shadow-sm"><CardHeader><CardTitle className="flex items-center gap-2 text-base"><Settings className="h-5 w-5 text-teal-700" />Integraties</CardTitle></CardHeader><CardContent className="space-y-2 text-sm text-slate-600"><p>In-app notificaties zijn actief voorbereid.</p><p>E-mail, pushnotificaties, kalenderexport en presentatie-export volgen later.</p></CardContent></Card>
      </div>
    </div>
  );

  const renderContent = () => {
    if (section === 'dashboard') return renderDashboard();
    if (section === 'planning') return renderPlanning(false);
    if (section === 'roster') return renderRoster();
    if (section === 'my-planning') return renderPlanning(true);
    if (section === 'setlists') return renderPlanning(false);
    if (section === 'songs') return renderSongs();
    if (section === 'team') return renderTeam();
    if (section === 'patterns') return renderPatterns();
    if (section === 'rehearsals') return renderRehearsals();
    if (section === 'files') return renderFiles();
    return renderSettings();
  };

  if (accessDenied) return <AccessDeniedState onBack={() => navigate('/worship', { replace: true })} />;

  return (
    <WorshipLayout>
      {loading ? <div className="space-y-4 animate-pulse"><div className="h-8 w-56 rounded bg-slate-200" /><div className="h-40 rounded-md bg-slate-200" /></div> : renderContent()}

      <Dialog open={serviceDialog} onOpenChange={(open) => {
        setServiceDialog(open);
        if (!open) {
          setEditingServiceId(null);
          setServiceForm(emptyService);
        }
      }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle>{editingServiceId ? 'Worshipdienst bewerken' : 'Nieuwe worshipdienst'}</DialogTitle></DialogHeader>
          <form onSubmit={saveService} className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Titel *</Label><Input required value={serviceForm.title} onChange={(e) => setServiceForm({ ...serviceForm, title: e.target.value })} placeholder="Zondagochtenddienst" /></div>
            <div><Label>Datum *</Label><Input type="date" required value={serviceForm.service_date} onChange={(e) => setServiceForm({ ...serviceForm, service_date: e.target.value })} /></div>
            <div><Label>Status</Label><select className="h-10 w-full rounded-md border border-slate-200 bg-white px-3" value={serviceForm.status} onChange={(e) => setServiceForm({ ...serviceForm, status: e.target.value })}>{Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div>
            <div><Label>Starttijd *</Label><Input type="time" required value={serviceForm.start_time} onChange={(e) => setServiceForm({ ...serviceForm, start_time: e.target.value })} /></div>
            <div><Label>Eindtijd *</Label><Input type="time" required value={serviceForm.end_time} onChange={(e) => setServiceForm({ ...serviceForm, end_time: e.target.value })} /></div>
            <div><Label>Locatie</Label><Input value={serviceForm.location} onChange={(e) => setServiceForm({ ...serviceForm, location: e.target.value })} /></div>
            <div><Label>Thema</Label><Input value={serviceForm.theme} onChange={(e) => setServiceForm({ ...serviceForm, theme: e.target.value })} /></div>
            <div><Label>Voorganger / spreker</Label><Input value={serviceForm.speaker} onChange={(e) => setServiceForm({ ...serviceForm, speaker: e.target.value })} /></div>
            <div><Label>Worshipleader</Label><select className="h-10 w-full rounded-md border border-slate-200 bg-white px-3" value={serviceForm.worship_leader_id} onChange={(e) => setServiceForm({ ...serviceForm, worship_leader_id: e.target.value })}><option value="">Nog niet gekozen</option>{team.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}</select></div>
            <div><Label>Zangdienst</Label><select className="h-10 w-full rounded-md border border-slate-200 bg-white px-3" value={serviceForm.zangdienst_user_id} onChange={(e) => setServiceForm({ ...serviceForm, zangdienst_user_id: e.target.value })}><option value="">Nog niet gekozen</option>{team.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}</select></div>
            <div className="sm:col-span-2"><Label>Notities</Label><Textarea value={serviceForm.notes} onChange={(e) => setServiceForm({ ...serviceForm, notes: e.target.value })} /></div>
            <DialogFooter className="sm:col-span-2"><Button type="button" variant="outline" onClick={() => { setServiceDialog(false); setEditingServiceId(null); setServiceForm(emptyService); }}>Annuleren</Button><Button type="submit" className="bg-teal-600 text-white hover:bg-teal-700">{editingServiceId ? 'Wijzigingen opslaan' : 'Dienst aanmaken'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-4xl">
          {selectedService && <>
            <DialogHeader><DialogTitle>{section === 'setlists' ? 'Setlist voorbereiden' : selectedService.title}</DialogTitle></DialogHeader>
            {section === 'setlists' && <p className="-mt-2 text-sm text-slate-500">{selectedService.title} · {formatDate(selectedService.service_date)}</p>}
            <div className="flex flex-wrap gap-4 rounded-md bg-slate-50 p-4 text-sm text-slate-600"><span>{formatDate(selectedService.service_date)}</span><span>{selectedService.start_time} - {selectedService.end_time}</span><span>{selectedService.location || 'Locatie volgt'}</span>{selectedService.zangdienst_user_id && <span>Zangdienst: {team.find((m) => m.id === selectedService.zangdienst_user_id)?.name || 'Onbekend teamlid'}</span>}<Badge>{STATUS_LABELS[selectedService.status]}</Badge></div>
            <div className="grid min-w-0 gap-6 lg:grid-cols-2">
              <section className={`min-w-0 ${section === 'setlists' ? 'lg:order-2' : ''}`}>
                <h3 className="mb-3 font-semibold text-slate-900">Teamindeling</h3>
                <div className="space-y-2">
                  {(selectedService.assignments || []).map((assignment) => (
                    <div key={assignment.id} className="rounded-md border border-slate-200 p-3">
                      <div className="flex items-start justify-between gap-2"><div><p className="font-medium text-slate-900">{assignment.name}</p><p className="text-sm text-slate-500">{assignment.function} · {RESPONSE_LABELS[assignment.response_status]}</p></div>{canManage && <Button variant="ghost" size="icon" onClick={() => removeAssignment(assignment.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>}</div>
                      {assignment.user_id === user?.id && <div className="mt-3 flex flex-wrap gap-2"><Button size="sm" variant="outline" onClick={() => respondToAssignment(assignment, 'bevestigd', 'beschikbaar')}><CheckCircle2 className="mr-1 h-4 w-4" />Beschikbaar</Button><Button size="sm" variant="outline" onClick={() => respondToAssignment(assignment, 'onder_voorbehoud', 'onder_voorbehoud')}>Onder voorbehoud</Button><Button size="sm" variant="outline" onClick={() => respondToAssignment(assignment, 'afgewezen', 'niet_beschikbaar')}>Niet beschikbaar</Button></div>}
                    </div>
                  ))}
                </div>
                {canManage && (
                  <form onSubmit={addAssignment} className="mt-4 space-y-3 rounded-md border border-dashed border-slate-300 p-3">
                    <select
                      className="h-10 w-full rounded-md border border-slate-200 bg-white px-3"
                      value={assignmentForm.function}
                      onChange={(e) => { setAssignmentForm({ ...assignmentForm, function: e.target.value, user_id: '' }); setAssignmentConflicts([]); setAssignmentOverride(false); }}
                    >
                      {FUNCTIONS.map((item) => <option key={item}>{item}</option>)}
                    </select>
                    <div className="max-h-52 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-1">
                      {!assignmentSuggestions.length && <p className="p-2 text-xs text-slate-400">Geen teamleden beschikbaar om te suggereren.</p>}
                      {assignmentSuggestions.map((candidate) => {
                        const hasHardIssue = candidate.reasons.some((r) => /Afwezig|al iets anders|Niet aangevinkt|niet beschikbaar/.test(r));
                        const selected = assignmentForm.user_id === candidate.user_id;
                        return (
                          <button
                            key={candidate.user_id}
                            type="button"
                            onClick={() => { setAssignmentForm({ ...assignmentForm, user_id: candidate.user_id }); setAssignmentConflicts([]); setAssignmentOverride(false); }}
                            className={`flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${selected ? 'bg-teal-50 ring-1 ring-teal-500' : 'hover:bg-slate-50'} ${hasHardIssue ? 'opacity-60' : ''}`}
                          >
                            <span className="font-medium text-slate-900">{candidate.name}</span>
                            {!!candidate.reasons.length && <span className="text-xs text-slate-500">{candidate.reasons.join(' · ')}</span>}
                          </button>
                        );
                      })}
                    </div>
                    {!!assignmentConflicts.length && (
                      <div className="space-y-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
                        <p className="font-medium">Dit teamlid heeft al iets anders op dit moment:</p>
                        <ul className="list-inside list-disc space-y-0.5">
                          {assignmentConflicts.map((conflict) => (
                            <li key={`${conflict.module}-${conflict.reference_id}`}>
                              {conflict.kind === 'absence'
                                ? `Afwezig${conflict.title ? ` (${conflict.title})` : ''}`
                                : `${CONFLICT_MODULE_LABELS[conflict.module] || conflict.module}: ${conflict.title} (${conflict.start_time}–${conflict.end_time})`}
                            </li>
                          ))}
                        </ul>
                        <label className="flex items-center gap-1.5 text-sm">
                          <Checkbox checked={assignmentOverride} onCheckedChange={(checked) => setAssignmentOverride(!!checked)} />
                          Toch indelen ondanks conflict
                        </label>
                        {assignmentOverride && (
                          <Input
                            placeholder="Reden voor uitzondering (optioneel)"
                            value={assignmentOverrideReason}
                            onChange={(e) => setAssignmentOverrideReason(e.target.value)}
                          />
                        )}
                      </div>
                    )}
                    <Button type="submit" size="sm" className="bg-teal-600 text-white hover:bg-teal-700" disabled={!!assignmentConflicts.length && !assignmentOverride}>
                      <Plus className="mr-1 h-4 w-4" />Indelen
                    </Button>
                  </form>
                )}
              </section>
              <section className={`min-w-0 ${section === 'setlists' ? 'lg:order-1' : ''}`}>
                <div className="mb-3 flex items-center justify-between">
                  <div><h3 className="font-semibold text-slate-900">Dienstvolgorde</h3><p className="text-xs text-slate-500">Liederen en andere onderdelen in uitvoeringsvolgorde.</p></div>
                  <Button variant="outline" size="sm" onClick={() => setShareDialogOpen(true)}><Share2 className="mr-1 h-4 w-4" />Deel setlist</Button>
                </div>
                <SetlistPanel service={selectedService} canManage={canManage} songs={songs} onServiceChange={updateSetlistService} onOpenItem={openSetlistSong} />
              </section>
            </div>
            {canManage && <DialogFooter><Button variant="outline" onClick={editService}>Dienstgegevens bewerken</Button><Button variant="destructive" onClick={deleteService}><Trash2 className="mr-2 h-4 w-4" />Dienst verwijderen</Button></DialogFooter>}
          </>}
        </DialogContent>
      </Dialog>

      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Setlist delen</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2">
            <div className="rounded-md border border-slate-200 bg-white p-4"><QRCodeSVG value={shareUrl} size={180} /></div>
            <p className="text-center text-xs text-slate-500">Scan de QR-code of deel de link. Geen account nodig om de setlist te bekijken.</p>
            <div className="grid w-full gap-2">
              <Button type="button" variant="outline" onClick={copyShareLink}><Copy className="mr-2 h-4 w-4" />Kopieer link</Button>
              <a href={`https://wa.me/?text=${encodeURIComponent(`Setlist ${selectedService?.title || ''}: ${shareUrl}`)}`} target="_blank" rel="noreferrer">
                <Button type="button" className="w-full bg-emerald-600 text-white hover:bg-emerald-700"><MessageCircle className="mr-2 h-4 w-4" />Deel via WhatsApp</Button>
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={songEditorOpen} onOpenChange={setSongEditorOpen}>
        <DialogContent className="max-h-[96vh] overflow-y-auto sm:max-w-[96vw]">
          <DialogHeader><DialogTitle>{editingSong ? 'ChordPro-lied bewerken' : 'Nieuw ChordPro-lied'}</DialogTitle></DialogHeader>
          <form onSubmit={saveSong} className="space-y-4">
            <div className="grid gap-3 rounded-md bg-slate-50 p-3 md:grid-cols-3">
              <div><Label>SongSelect ID</Label><Input value={songIntegrations.songselect_id} onChange={(event) => setSongIntegrations({ ...songIntegrations, songselect_id: event.target.value })} /></div>
              <div><Label>Uitgever</Label><Input value={songIntegrations.publisher} onChange={(event) => setSongIntegrations({ ...songIntegrations, publisher: event.target.value })} /></div>
              <div><Label>Licentie</Label><Input value={songIntegrations.license} onChange={(event) => setSongIntegrations({ ...songIntegrations, license: event.target.value })} /></div>
              <div><Label>Bron</Label><Input value={songIntegrations.source} onChange={(event) => setSongIntegrations({ ...songIntegrations, source: event.target.value })} /></div>
              <div><Label>Google Drive FileId</Label><Input value={songIntegrations.google_drive_file_id} onChange={(event) => setSongIntegrations({ ...songIntegrations, google_drive_file_id: event.target.value })} /></div>
              <div><Label>Google Drive FolderId</Label><Input value={songIntegrations.google_drive_folder_id} onChange={(event) => setSongIntegrations({ ...songIntegrations, google_drive_folder_id: event.target.value })} /></div>
              <div className="md:col-span-2"><Label>PDF-link (Google Drive/Dropbox deel-link)</Label><Input value={songIntegrations.pdf_link_url} onChange={(event) => setSongIntegrations({ ...songIntegrations, pdf_link_url: event.target.value })} placeholder="https://drive.google.com/... of https://www.dropbox.com/..." /></div>
              <div><Label>Label voor PDF-link</Label><Input value={songIntegrations.pdf_link_label} onChange={(event) => setSongIntegrations({ ...songIntegrations, pdf_link_label: event.target.value })} placeholder="Bijv. Songtekst PDF" /></div>
            </div>
            {!draftDocument.validation.valid && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{draftDocument.validation.errors.join(' ')}</div>}
            {!!draftDocument.validation.warnings.length && <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">{draftDocument.validation.warnings.join(' ')}</div>}
            <ChordProEditor key={editingSong?.id || 'new'} value={chordProDraft} onChange={setChordProDraft} initialKey={editingSong?.default_key} />
            <DialogFooter className="max-sm:sticky max-sm:bottom-0 max-sm:z-10 max-sm:-mx-6 max-sm:-mb-6 max-sm:flex-row max-sm:gap-2 max-sm:border-t max-sm:bg-white max-sm:px-4 max-sm:py-3 max-sm:pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]"><Button type="button" variant="outline" className="max-sm:h-11 max-sm:flex-1" onClick={() => setSongEditorOpen(false)}>Annuleren</Button><Button type="submit" disabled={!draftDocument.validation.valid} className="bg-teal-600 text-white hover:bg-teal-700 max-sm:h-11 max-sm:flex-[2]">ChordPro opslaan</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={songViewerOpen} onOpenChange={setSongViewerOpen}>
        <DialogContent className="max-h-[96vh] overflow-hidden p-0 sm:max-w-5xl">
          <DialogHeader className="sr-only"><DialogTitle>{viewingSong?.title || 'ChordPro-lied'}</DialogTitle></DialogHeader>
          {viewingSong && <ChordProRenderer source={viewingSong.chordpro || `{title: ${viewingSong.title}}\n\n${viewingSong.lyrics || ''}`} initialKey={viewingSong.viewerKey} driveFileId={viewingSong.google_drive_file_id} driveFolderId={viewingSong.google_drive_folder_id} sourceFileUrl={viewingSong.source_file_url} sourceFileName={viewingSong.source_file_name} pdfLinkUrl={viewingSong.pdf_link_url} pdfLinkLabel={viewingSong.pdf_link_label} />}
        </DialogContent>
      </Dialog>

      <WorshipImportWizard open={importWizardOpen} onOpenChange={setImportWizardOpen} onSaved={() => loadData()} />

      <Dialog open={rehearsalDialog} onOpenChange={setRehearsalDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg"><DialogHeader><DialogTitle>Repetitie plannen</DialogTitle></DialogHeader><form onSubmit={saveRehearsal} className="grid gap-4 sm:grid-cols-2"><div><Label>Datum *</Label><Input type="date" required value={rehearsalForm.rehearsal_date} onChange={(e) => setRehearsalForm({ ...rehearsalForm, rehearsal_date: e.target.value })} /></div><div><Label>Locatie</Label><Input value={rehearsalForm.location} onChange={(e) => setRehearsalForm({ ...rehearsalForm, location: e.target.value })} /></div><div><Label>Starttijd *</Label><Input type="time" required value={rehearsalForm.start_time} onChange={(e) => setRehearsalForm({ ...rehearsalForm, start_time: e.target.value })} /></div><div><Label>Eindtijd *</Label><Input type="time" required value={rehearsalForm.end_time} onChange={(e) => setRehearsalForm({ ...rehearsalForm, end_time: e.target.value })} /></div><div className="sm:col-span-2"><Label>Gekoppelde dienst</Label><select className="h-10 w-full rounded-md border border-slate-200 bg-white px-3" value={rehearsalForm.service_id} onChange={(e) => setRehearsalForm({ ...rehearsalForm, service_id: e.target.value })}><option value="">Geen dienst gekoppeld</option>{services.map((service) => <option key={service.id} value={service.id}>{service.title} · {formatDate(service.service_date)}</option>)}</select></div><div className="sm:col-span-2"><Label>Liederen om te oefenen</Label><div className="mt-1 max-h-40 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2">{songs.map((song) => <label key={song.id} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50"><input type="checkbox" checked={rehearsalForm.song_ids.includes(song.id)} onChange={() => toggleRehearsalSong(song.id)} />{song.title}</label>)}{!songs.length && <p className="px-2 py-1 text-sm text-slate-500">Nog geen liederen in de bibliotheek.</p>}</div></div><div className="sm:col-span-2"><Label>Notities</Label><Textarea value={rehearsalForm.notes} onChange={(e) => setRehearsalForm({ ...rehearsalForm, notes: e.target.value })} /></div><DialogFooter className="sm:col-span-2"><Button type="button" variant="outline" onClick={() => setRehearsalDialog(false)}>Annuleren</Button><Button type="submit" className="bg-teal-600 text-white hover:bg-teal-700">Plannen</Button></DialogFooter></form></DialogContent>
      </Dialog>

      <Dialog open={addTeamMemberDialog} onOpenChange={setAddTeamMemberDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
          <DialogHeader><DialogTitle>Teamlid toevoegen</DialogTitle></DialogHeader>
          <div className="flex gap-1 rounded-md bg-slate-100 p-1">
            <button type="button" onClick={() => { setAddTeamMemberSource('team'); setInviteResult(null); }} className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium ${addTeamMemberSource === 'team' ? 'bg-white shadow-sm' : 'text-slate-500'}`}>Bestaand teamlid</button>
            <button type="button" onClick={() => { setAddTeamMemberSource('member'); setInviteResult(null); }} className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium ${addTeamMemberSource === 'member' ? 'bg-white shadow-sm' : 'text-slate-500'}`}>Gemeentelid</button>
          </div>

          {addTeamMemberSource === 'team' && (
            <form onSubmit={addExistingTeamMember} className="space-y-3">
              <p className="text-sm text-slate-500">Kies iemand die al een Zoea-account heeft (via Team algemeen).</p>
              <select required className="h-10 w-full rounded-md border border-slate-200 bg-white px-3" value={selectedOrgUserId} onChange={(e) => setSelectedOrgUserId(e.target.value)}>
                <option value="">Kies teamlid</option>
                {availableOrgTeamMembers.map((member) => <option key={member.id} value={member.id}>{member.name} ({(member.roles || []).join(', ')})</option>)}
              </select>
              {!availableOrgTeamMembers.length && <p className="text-xs text-slate-400">Iedereen uit Team algemeen staat al in het worshipteam.</p>}
              <DialogFooter><Button type="button" variant="outline" onClick={() => setAddTeamMemberDialog(false)}>Sluiten</Button><Button type="submit" className="bg-teal-600 text-white hover:bg-teal-700">Toevoegen</Button></DialogFooter>
            </form>
          )}

          {addTeamMemberSource === 'member' && (
            inviteResult ? (
              <div className="space-y-3">
                <div className="rounded-md bg-emerald-50 p-3 text-sm text-emerald-800">Uitnodiging verstuurd. Deel eventueel ook deze link:</div>
                <Input readOnly value={inviteResult} onFocus={(e) => e.target.select()} />
                <DialogFooter><Button type="button" onClick={() => setAddTeamMemberDialog(false)} className="bg-teal-600 text-white hover:bg-teal-700">Sluiten</Button></DialogFooter>
              </div>
            ) : (
              <form onSubmit={inviteOrgMember} className="space-y-3">
                <p className="text-sm text-slate-500">Kies een gemeentelid uit Ledenbestand. Ze krijgen een e-mail om zelf een wachtwoord in te stellen, en loggen daarna alleen in bij Worship Planning.</p>
                <select required className="h-10 w-full rounded-md border border-slate-200 bg-white px-3" value={selectedOrgMemberId} onChange={(e) => setSelectedOrgMemberId(e.target.value)}>
                  <option value="">Kies gemeentelid</option>
                  {availableOrgMembers.map((member) => <option key={member.id} value={member.id} disabled={!member.email}>{member.name}{!member.email ? ' (geen e-mailadres)' : ''}</option>)}
                </select>
                {!availableOrgMembers.length && <p className="text-xs text-slate-400">Geen (nieuwe) gemeenteleden gevonden in Ledenbestand.</p>}
                <DialogFooter><Button type="button" variant="outline" onClick={() => setAddTeamMemberDialog(false)}>Sluiten</Button><Button type="submit" className="bg-teal-600 text-white hover:bg-teal-700">Uitnodigen</Button></DialogFooter>
              </form>
            )
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={prefsDialogOpen} onOpenChange={setPrefsDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>Voorkeuren — {prefsMember?.name}</DialogTitle></DialogHeader>
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">Streef per maand</Label>
                <Input type="number" min="0" value={prefsForm.target_per_month} onChange={(e) => setPrefsForm({ ...prefsForm, target_per_month: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Minimum per maand</Label>
                <Input type="number" min="0" value={prefsForm.min_per_month} onChange={(e) => setPrefsForm({ ...prefsForm, min_per_month: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Maximum per maand</Label>
                <Input type="number" min="0" value={prefsForm.max_per_month} onChange={(e) => setPrefsForm({ ...prefsForm, max_per_month: e.target.value })} />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Beschikbaar voor rollen</Label>
              <div className="flex flex-wrap gap-3">
                {FUNCTIONS.map((fn) => (
                  <label key={fn} className="flex items-center gap-1.5 text-sm text-slate-700">
                    <Checkbox checked={prefsForm.eligible_functions.includes(fn)} onCheckedChange={() => togglePrefsFunction(fn)} />
                    {fn}
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-400">Niets aangevinkt = geen restrictie (alle rollen mogelijk).</p>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Standaard niet beschikbaar op</Label>
              <div className="flex flex-wrap gap-3">
                {WEEKDAYS.map((day) => (
                  <label key={day.value} className="flex items-center gap-1.5 text-sm text-slate-700">
                    <Checkbox checked={prefsForm.unavailable_weekdays.includes(day.value)} onCheckedChange={() => togglePrefsWeekday(day.value)} />
                    {day.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Voorkeur diensttijd</Label>
              <Select value={prefsForm.preferred_service_time || 'none'} onValueChange={(v) => setPrefsForm({ ...prefsForm, preferred_service_time: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Geen voorkeur" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Geen voorkeur</SelectItem>
                  <SelectItem value="morning">Liever ochtenddienst</SelectItem>
                  <SelectItem value="evening">Liever avonddienst</SelectItem>
                  <SelectItem value="any">Beide prima</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Notities</Label>
              <Textarea rows={2} value={prefsForm.notes} onChange={(e) => setPrefsForm({ ...prefsForm, notes: e.target.value })} />
            </div>

            <div className="space-y-2 border-t border-slate-100 pt-4">
              <Label className="text-xs">Afwezigheid / verlof</Label>
              <div className="space-y-2">
                {absences.map((absence) => (
                  <div key={absence.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm">
                    <span>{absence.start_date} — {absence.end_date}{absence.reason ? ` · ${absence.reason}` : ''}</span>
                    <Button variant="ghost" size="icon" onClick={() => deleteAbsence(absence.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                  </div>
                ))}
                {!absences.length && <p className="text-xs text-slate-400">Geen afwezigheid geregistreerd.</p>}
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <Input type="date" value={absenceForm.start_date} onChange={(e) => setAbsenceForm({ ...absenceForm, start_date: e.target.value })} />
                <Input type="date" value={absenceForm.end_date} onChange={(e) => setAbsenceForm({ ...absenceForm, end_date: e.target.value })} />
              </div>
              <div className="flex gap-2">
                <Input placeholder="Reden (optioneel)" value={absenceForm.reason} onChange={(e) => setAbsenceForm({ ...absenceForm, reason: e.target.value })} />
                <Button type="button" variant="outline" onClick={addAbsence}>Toevoegen</Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPrefsDialogOpen(false)}>Sluiten</Button>
            <Button type="button" disabled={prefsSaving} onClick={savePreferences} className="bg-teal-600 text-white hover:bg-teal-700">
              {prefsSaving ? 'Opslaan...' : 'Voorkeuren opslaan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={patternDialog} onOpenChange={(open) => {
        setPatternDialog(open);
        if (!open) {
          setEditingPatternId(null);
          setPatternForm(emptyPattern);
        }
      }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>{editingPatternId ? 'Patroon bewerken' : 'Nieuw dienstpatroon'}</DialogTitle></DialogHeader>
          <form onSubmit={savePattern} className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Titel *</Label><Input required value={patternForm.title} onChange={(e) => setPatternForm({ ...patternForm, title: e.target.value })} placeholder="Zondagochtenddienst" /></div>
            <div>
              <Label>Weekdag *</Label>
              <select className="h-10 w-full rounded-md border border-slate-200 bg-white px-3" value={patternForm.weekday} onChange={(e) => setPatternForm({ ...patternForm, weekday: Number(e.target.value) })}>
                {WEEKDAYS.map((day) => <option key={day.value} value={day.value}>{day.label}</option>)}
              </select>
            </div>
            <div className="flex items-end gap-2 pb-1">
              <label className="flex items-center gap-1.5 text-sm text-slate-700">
                <Checkbox checked={patternForm.is_active} onCheckedChange={(checked) => setPatternForm({ ...patternForm, is_active: !!checked })} />
                Actief
              </label>
            </div>
            <div><Label>Starttijd *</Label><Input type="time" required value={patternForm.start_time} onChange={(e) => setPatternForm({ ...patternForm, start_time: e.target.value })} /></div>
            <div><Label>Eindtijd *</Label><Input type="time" required value={patternForm.end_time} onChange={(e) => setPatternForm({ ...patternForm, end_time: e.target.value })} /></div>
            <div><Label>Locatie</Label><Input value={patternForm.location} onChange={(e) => setPatternForm({ ...patternForm, location: e.target.value })} /></div>
            <div>
              <Label>Standaard worshipleader</Label>
              <select className="h-10 w-full rounded-md border border-slate-200 bg-white px-3" value={patternForm.default_worship_leader_id} onChange={(e) => setPatternForm({ ...patternForm, default_worship_leader_id: e.target.value })}>
                <option value="">Nog niet gekozen</option>
                {team.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}
              </select>
            </div>
            <DialogFooter className="sm:col-span-2">
              <Button type="button" variant="outline" onClick={() => { setPatternDialog(false); setEditingPatternId(null); setPatternForm(emptyPattern); }}>Annuleren</Button>
              <Button type="submit" className="bg-teal-600 text-white hover:bg-teal-700">{editingPatternId ? 'Wijzigingen opslaan' : 'Patroon aanmaken'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={generateDialog} onOpenChange={(open) => {
        setGenerateDialog(open);
        if (!open) { setGeneratingPattern(null); setGenerateResult(null); }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Genereer maandrooster — {generatingPattern?.title}</DialogTitle></DialogHeader>
          <form onSubmit={submitGenerate} className="space-y-4">
            <p className="text-sm text-slate-500">
              Maakt voor elke {WEEKDAYS.find((w) => w.value === generatingPattern?.weekday)?.label || ''} in de gekozen maand een concept-dienst aan. Bestaande diensten uit dit patroon worden overgeslagen.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Jaar</Label><Input type="number" min="2000" max="2100" required value={generateForm.year} onChange={(e) => setGenerateForm({ ...generateForm, year: e.target.value })} /></div>
              <div>
                <Label>Maand</Label>
                <select className="h-10 w-full rounded-md border border-slate-200 bg-white px-3" value={generateForm.month} onChange={(e) => setGenerateForm({ ...generateForm, month: Number(e.target.value) })}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>{new Intl.DateTimeFormat('nl-NL', { month: 'long' }).format(new Date(2000, m - 1, 1))}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Data overslaan (optioneel, komma-gescheiden, YYYY-MM-DD)</Label>
              <Input value={generateForm.skip_dates} onChange={(e) => setGenerateForm({ ...generateForm, skip_dates: e.target.value })} placeholder="2026-07-19, 2026-07-26" />
            </div>
            {generateResult && (
              <div className="space-y-2 rounded-md bg-slate-50 p-3 text-sm">
                <p><span className="font-medium text-teal-700">{generateResult.created.length}</span> aangemaakt: {generateResult.created.join(', ') || '-'}</p>
                <p><span className="font-medium text-slate-500">{generateResult.skipped_existing.length}</span> al bestaand (overgeslagen): {generateResult.skipped_existing.join(', ') || '-'}</p>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setGenerateDialog(false)}>Sluiten</Button>
              <Button type="submit" disabled={generating} className="bg-teal-600 text-white hover:bg-teal-700">{generating ? 'Genereren...' : 'Genereren'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </WorshipLayout>
  );
};

export default WorshipPage;
